import React, { useEffect, useState } from "react";
import { Eye, Pencil, Trash2, Plus } from "lucide-react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import {
  getAllTerms,
  createTerms,
  updateTerms,
  deleteTerms,
} from "../../services/TermCondition/Term.service";
import "./TermPage.css";

interface Terms {
  id: number;
  title: string;
  content: string;
  version: string;
  isActive: boolean;
  createdAt: string;
}

const TermsPage = () => {
  const [terms, setTerms] = useState<Terms[]>([]);
  const [filtered, setFiltered] = useState<Terms[]>([]);
  const [search, setSearch] = useState("");

  const [selected, setSelected] = useState<Terms | null>(null);
  const [showModal, setShowModal] = useState(false);

  const [viewItem, setViewItem] = useState<Terms | null>(null);

  const [form, setForm] = useState({
    title: "",
    content: "",
    version: "",
  });

  // Default Terms & Conditions Content
  const defaultContent = `
    <h2>SonaChandiWale — Additional / Revised Commercial & Privacy Clauses</h2>

    <h3>1. QUOTATIONS ARE ESTIMATES ONLY</h3>
    <p>Any quotation, estimate, indicative price, rate, calculation, price range, match result or commercial information displayed, generated or communicated through SonaChandiWale ("Quotation") is provided solely for estimation, discussion and commercial evaluation purposes.</p>
    <p>A Quotation:</p>
    <ul>
      <li>is not an offer of sale;</li>
      <li>is not an acceptance of an offer;</li>
      <li>is not a confirmed order;</li>
      <li>does not constitute a completed sale or purchase;</li>
      <li>does not create a binding Transaction;</li>
      <li>does not guarantee availability of inventory;</li>
      <li>does not guarantee the quoted price;</li>
      <li>does not constitute a tax invoice; and</li>
      <li>does not by itself create any tax liability arising from a completed sale.</li>
    </ul>
    <p>The Quotation is subject to subsequent confirmation of commercial terms between the relevant parties.</p>

    <h3>2. MARKET-BASED AND VARIABLE PRICES</h3>
    <p>Prices of jewellery raw materials, precious metals, components and other Products may fluctuate due to market conditions, metal rates, availability, purity, quantity, specifications, manufacturing requirements, wastage, making charges, logistics, taxes and other commercial factors.</p>
    <p>Accordingly, any rate displayed or communicated during the quotation or estimation stage may change at any time before final confirmation of the Transaction.</p>
    <p>SonaChandiWale shall not be responsible for any difference between an indicative/estimated Quotation and the final commercial price.</p>
    <p>The final price applicable to a Transaction shall be the price mutually confirmed for that specific Transaction at the time of finalisation.</p>

    <h3>3. WHEN A TRANSACTION IS CREATED</h3>
    <p>For the purposes of the Platform, a Quotation, requirement, inventory match, price discussion or preliminary communication shall not by itself constitute a Transaction.</p>
    <p>A Transaction shall be considered finalised only after the relevant commercial terms have been mutually confirmed through the applicable communication and confirmation process and the transaction has been accepted/closed through the process prescribed by SonaChandiWale.</p>
    <p>Such finalisation may involve multiple telephone calls, WhatsApp messages, electronic communications, negotiations, confirmations, verification steps and other communications between the relevant parties and/or SonaChandiWale representatives.</p>
    <p>The Company may maintain records of such communications and confirmations for transaction management, dispute resolution, fraud prevention, accounting, compliance and legal purposes.</p>

    <h3>4. DELIVERY AND TRANSACTION RECORD</h3>
    <p>Following final confirmation of the commercial terms, the Product may be processed for fulfilment and delivery.</p>
    <p>Upon completion of the applicable delivery/fulfilment process, the relevant transaction may be marked as a "Transaction" / "Completed Transaction" within SonaChandiWale's records.</p>
    <p>The Company may retain records relating to the Transaction including:</p>
    <ul>
      <li>Product details;</li>
      <li>quantity;</li>
      <li>agreed price;</li>
      <li>applicable charges;</li>
      <li>communications;</li>
      <li>confirmations;</li>
      <li>delivery information;</li>
      <li>invoices/documents;</li>
      <li>payment records; and</li>
      <li>other transaction-related information.</li>
    </ul>
    <p>The above record-keeping mechanism is for operational, accounting, compliance, audit, dispute-resolution and evidentiary purposes and does not alter the actual legal relationship between the relevant parties.</p>

    <h3>5. NO TAX LIABILITY MERELY FROM A QUOTATION</h3>
    <p>A quotation, estimate, price indication, buyer requirement, seller inventory listing, match or preliminary negotiation shall not, by itself, be treated as a completed sale or purchase.</p>
    <p>Applicable taxes, GST, duties, charges or other statutory obligations relating to the actual underlying sale/Transaction shall arise and be handled in accordance with applicable law and the circumstances of the completed Transaction.</p>
    <p>The applicable Seller and/or other legally responsible party shall remain responsible for its own tax, GST, invoicing and statutory obligations.</p>
    <p>Nothing in this clause is intended to determine or override the tax treatment prescribed by applicable law.</p>

    <h3>6. MULTIPLE COMMUNICATIONS AND FINAL CONFIRMATION</h3>
    <p>The parties acknowledge that commercial Transactions on the Platform may involve several stages of communication.</p>
    <p>These may include:</p>
    <ol>
      <li>Buyer requirement;</li>
      <li>inventory matching;</li>
      <li>indicative quotation;</li>
      <li>price discussion;</li>
      <li>product/specification clarification;</li>
      <li>quantity confirmation;</li>
      <li>negotiation;</li>
      <li>additional telephone calls;</li>
      <li>WhatsApp or electronic communication;</li>
      <li>final price confirmation;</li>
      <li>payment/settlement arrangements;</li>
      <li>fulfilment;</li>
      <li>delivery; and</li>
      <li>completion of the Transaction.</li>
    </ol>
    <p>No individual message, quotation or preliminary communication should be interpreted as the final Transaction unless it is expressly confirmed as such through the applicable SonaChandiWale process.</p>

    <h3>7. PRODUCT PURITY, AUTHENTICITY AND QUALITY DISCLAIMER</h3>
    <p>SonaChandiWale operates as a technology-enabled intermediary/facilitation platform.</p>
    <p>Unless SonaChandiWale expressly provides a separate written testing, verification or certification service for a specific Product, SonaChandiWale does not independently guarantee or warrant the purity, authenticity, genuineness, composition, quality, weight, grade, origin, workmanship, condition or legality of any Product listed, matched, quoted or supplied through the Platform.</p>
    <p>SonaChandiWale shall not be responsible for Products that are:</p>
    <ul>
      <li>fake;</li>
      <li>counterfeit;</li>
      <li>adulterated;</li>
      <li>impure;</li>
      <li>underweight;</li>
      <li>incorrectly described;</li>
      <li>materially different from the description;</li>
      <li>defective;</li>
      <li>damaged;</li>
      <li>unlawfully sourced; or</li>
      <li>otherwise inconsistent with the Seller's representations,</li>
    </ul>
    <p>except to the extent that applicable law imposes a non-excludable obligation upon SonaChandiWale or the Company has expressly undertaken a specific responsibility in writing.</p>
    <p>The Seller remains responsible for the accuracy, legality, authenticity, quality, purity, quantity and specifications of Products supplied by the Seller.</p>

    <h3>8. SELLER REPRESENTATIONS REGARDING PRODUCTS</h3>
    <p>Every Seller represents and warrants that the Products supplied by it:</p>
    <ul>
      <li>are lawfully possessed by the Seller;</li>
      <li>are lawfully capable of being sold;</li>
      <li>are accurately described;</li>
      <li>are not knowingly counterfeit or fraudulent;</li>
      <li>comply with applicable laws;</li>
      <li>meet the specifications represented by the Seller;</li>
      <li>have the represented quantity/weight and characteristics; and</li>
      <li>are not subject to undisclosed third-party claims.</li>
    </ul>
    <p>The Seller shall be solely responsible for any breach of these representations, subject to applicable law.</p>
    <p>SonaChandiWale may suspend or remove a Seller, listing or Transaction where it receives a complaint or otherwise reasonably suspects fraud, misrepresentation, counterfeit goods, unlawful activity or other serious violation.</p>

    <h3>9. NO ASSUMPTION OF SELLER LIABILITY</h3>
    <p>The fact that SonaChandiWale:</p>
    <ul>
      <li>displays a Product;</li>
      <li>matches a Product;</li>
      <li>communicates Product information;</li>
      <li>generates a Quotation;</li>
      <li>coordinates communication;</li>
      <li>facilitates payment;</li>
      <li>coordinates delivery;</li>
      <li>maintains transaction records; or</li>
      <li>provides administrative support</li>
    </ul>
    <p>shall not, by itself, mean that SonaChandiWale has manufactured, inspected, owned, possessed, warranted or guaranteed the Product.</p>

    <h3>10. SELLER INFORMATION — CONFIDENTIALITY</h3>
    <p>SonaChandiWale treats Seller information as confidential business information, subject to applicable law and legitimate operational requirements.</p>
    <p>Seller information, including where applicable:</p>
    <ul>
      <li>Seller name;</li>
      <li>contact number;</li>
      <li>personal contact information;</li>
      <li>business contact information;</li>
      <li>business address;</li>
      <li>inventory information;</li>
      <li>internal pricing;</li>
      <li>supplier information;</li>
      <li>transaction history;</li>
      <li>commercial terms;</li>
      <li>bank/payment information;</li>
      <li>documents;</li>
      <li>GST/business information; and</li>
      <li>other confidential business information</li>
    </ul>
    <p>shall not be disclosed to another User merely because that User has submitted a requirement or is matched with the Seller.</p>
    <p>SonaChandiWale shall use reasonable measures to prevent unauthorised disclosure of Seller information.</p>

    <h3>11. BUYER INFORMATION — CONFIDENTIALITY</h3>
    <p>Buyer information shall similarly be treated as confidential.</p>
    <p>Seller Users shall not receive Buyer's personal or business information except to the extent reasonably necessary for completing a Transaction, complying with law, providing delivery/fulfilment services, preventing fraud or where the Buyer has otherwise authorised such disclosure.</p>
    <p>The Company may disclose information where required or permitted by applicable law or where necessary for legitimate Platform operations.</p>

    <h3>12. NO DIRECT ACCESS TO OTHER USER DATA</h3>
    <p>Users shall not attempt to:</p>
    <ul>
      <li>identify another User whose identity has been intentionally withheld;</li>
      <li>obtain another User's telephone number;</li>
      <li>obtain another User's address;</li>
      <li>obtain confidential business information;</li>
      <li>scrape Platform data;</li>
      <li>reverse-engineer matching information;</li>
      <li>use technical means to bypass privacy controls;</li>
      <li>impersonate another User; or</li>
      <li>otherwise circumvent SonaChandiWale's privacy and access controls.</li>
    </ul>
    <p>Any attempt to obtain or misuse such information may result in immediate suspension or termination of the relevant account, in addition to any other remedies available to the Company under law.</p>

    <h3>13. LIMITED DISCLOSURE FOR TRANSACTION FULFILMENT</h3>
    <p>Nothing in the confidentiality provisions prevents SonaChandiWale from sharing information strictly necessary to:</p>
    <ul>
      <li>process a Transaction;</li>
      <li>arrange delivery;</li>
      <li>process payment;</li>
      <li>issue required documentation;</li>
      <li>conduct KYC/business verification;</li>
      <li>prevent fraud;</li>
      <li>resolve a dispute;</li>
      <li>comply with taxation requirements;</li>
      <li>comply with a lawful government request;</li>
      <li>comply with a court/authority order; or</li>
      <li>protect the rights, property or security of SonaChandiWale, its Users or third parties.</li>
    </ul>
    <p>Only information reasonably necessary for the relevant purpose should be disclosed, subject to applicable law.</p>

    <h3>14. DATA IS NOT SOLD</h3>
    <p>SonaChandiWale shall not sell a User's personal information to third parties merely for the purpose of selling personal information.</p>
    <p>Personal and business information may, however, be processed or shared where reasonably necessary for providing the Platform, completing Transactions, payment processing, logistics, technology infrastructure, security, fraud prevention, legal compliance, analytics, customer support or other legitimate purposes described in the Privacy Policy.</p>
    <p>The detailed categories of information collected, purposes of processing, retention, rights and permitted disclosures shall be governed by the SonaChandiWale Privacy Policy.</p>

    <h3>15. INTERNAL ACCESS TO USER INFORMATION</h3>
    <p>Access to confidential User information within SonaChandiWale should be limited to authorised personnel, contractors and service providers who reasonably require such access to perform their functions.</p>
    <p>The Company may implement technical and organisational safeguards intended to protect User information against unauthorised access, misuse, alteration, disclosure or destruction.</p>
    <p>No security system can guarantee absolute security, and the Company does not represent that information can never be accessed by an unauthorised person despite reasonable safeguards.</p>

    <h3>16. PLATFORM AS AN INTERMEDIARY</h3>
    <p>SonaChandiWale's primary role is to facilitate the discovery, matching, communication, coordination and completion of commercial opportunities between Buyers and Sellers.</p>
    <p>The Platform does not guarantee that:</p>
    <ul>
      <li>every requirement will receive a match;</li>
      <li>every matched Seller will complete a Transaction;</li>
      <li>a Seller will remain available;</li>
      <li>a Quotation will remain valid;</li>
      <li>a Product will remain available;</li>
      <li>the final negotiated price will equal the Quotation;</li>
      <li>a Buyer will purchase;</li>
      <li>a Seller will supply;</li>
      <li>the Product will meet the Buyer's expectations; or</li>
      <li>a Transaction will be successfully completed.</li>
    </ul>
    <p>The Company shall use reasonable efforts to operate the Platform but does not guarantee any particular commercial outcome.</p>

    <h3>17. COMMUNICATION RECORDS</h3>
    <p>Where legally permissible, SonaChandiWale may retain records of communications occurring through or in connection with the Platform, including relevant:</p>
    <ul>
      <li>calls;</li>
      <li>messages;</li>
      <li>WhatsApp communications;</li>
      <li>emails;</li>
      <li>confirmations;</li>
      <li>quotations;</li>
      <li>order information;</li>
      <li>payment records; and</li>
      <li>delivery records.</li>
    </ul>
    <p>Such records may be used for:</p>
    <ul>
      <li>Transaction administration;</li>
      <li>customer support;</li>
      <li>dispute resolution;</li>
      <li>fraud prevention;</li>
      <li>quality monitoring;</li>
      <li>accounting;</li>
      <li>compliance;</li>
      <li>legal proceedings; and</li>
      <li>protection of the Company and Users.</li>
    </ul>

    <h3>18. IMPORTANT DISTINCTION BETWEEN QUOTATION AND TRANSACTION</h3>
    <p>For avoidance of doubt:</p>
    <ul>
      <li>Requirement ≠ Quotation</li>
      <li>Quotation ≠ Order</li>
      <li>Quotation ≠ Sale</li>
      <li>Quotation ≠ Purchase</li>
      <li>Quotation ≠ Tax Invoice</li>
      <li>Quotation ≠ Completed Transaction</li>
    </ul>
    <p>A Quotation is an indicative commercial estimate only.</p>
    <p>The final commercial terms may change before the Transaction is finalised.</p>
    <p>The Transaction becomes subject to the final agreed commercial terms and applicable law only when the relevant commercial process has been completed and the deal is confirmed/closed.</p>

    <h3>19. NO GUARANTEE OF INFORMATION ACCURACY</h3>
    <p>SonaChandiWale may rely on information supplied by Sellers, Buyers, third-party service providers, market sources or other external systems.</p>
    <p>While the Company may undertake reasonable checks or verification where appropriate, it does not guarantee that all information available on the Platform is complete, current, accurate or error-free.</p>
    <p>Users should independently verify material commercial information before finalising a Transaction.</p>

    <h3>20. PREVAILING COMMERCIAL TERMS</h3>
    <p>Where the final Transaction terms differ from an earlier Quotation, estimate or discussion, the latest mutually confirmed final commercial terms shall govern the Transaction, subject to applicable law and applicable written agreements.</p>
    <p>Earlier estimates shall not be relied upon as the final price once superseded.</p>

    <hr />

    <h3>NOTICE TO USERS</h3>
    <p><strong>Please carefully review the final commercial terms before confirming a Transaction. Market prices and Product availability may change between quotation and final confirmation. SonaChandiWale does not guarantee the purity, authenticity or quality of Products supplied by independent Sellers unless expressly stated otherwise in writing.</strong></p>
  `;

  // ✅ FETCH
  const fetchTerms = async () => {
    try {
      const res = await getAllTerms();
      const data = res?.data?.data || [];
      setTerms(data);
      setFiltered(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchTerms();
  }, []);

  // 🔍 SEARCH
  useEffect(() => {
    let data = [...terms];

    if (search) {
      data = data.filter((t) =>
        `${t.title} ${t.version}`
          .toLowerCase()
          .includes(search.toLowerCase())
      );
    }

    setFiltered(data);
  }, [search, terms]);

  // ➕ CREATE
  const handleCreate = () => {
    setSelected(null);
    setForm({
      title: "SonaChandiWale — Terms & Conditions",
      content: defaultContent,
      version: "2.0",
    });
    setShowModal(true);
  };

  // ✏️ EDIT
  const handleEdit = (item: Terms) => {
    setSelected(item);
    setForm({
      title: item.title,
      content: item.content,
      version: item.version,
    });
    setShowModal(true);
  };

  // 💾 SAVE
  const handleSave = async () => {
    try {
      if (selected) {
        await updateTerms(selected.id, form);
        alert("Updated successfully");
      } else {
        await createTerms(form);
        alert("Created successfully");
      }

      setShowModal(false);
      fetchTerms();
    } catch (err) {
      console.error(err);
      alert("Error");
    }
  };

  // ❌ DELETE
  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this term?")) return;

    try {
      await deleteTerms(id);
      fetchTerms();
    } catch (err) {
      console.error(err);
    }
  };

  // CKEditor configuration
  const editorConfig = {
    toolbar: [
      'heading',
      '|',
      'bold',
      'italic',
      'underline',
      'strikethrough',
      '|',
      'bulletedList',
      'numberedList',
      '|',
      'alignment',
      '|',
      'link',
      '|',
      'undo',
      'redo'
    ],
    heading: {
      options: [
        { model: 'paragraph' as const, title: 'Paragraph', class: 'ck-heading_paragraph' },
        { model: 'heading1' as const, view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
        { model: 'heading2' as const, view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
        { model: 'heading3' as const, view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' }
      ]
    }
  };

  return (
    <div className="employee-container">

      {/* HEADER */}
      <div className="header-bar">
        <div className="filters-row">

          <input
            type="text"
            placeholder="Search by title or version..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="filter-input"
          />

          <button className="add-btn" onClick={handleCreate}>
            <Plus size={16} /> Add Terms
          </button>

        </div>
      </div>

      {/* TABLE */}
      <table className="employee-table">
        <thead>
          <tr>
            
            <th>Title</th>
            <th>Version</th>
            <th>Status</th>
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {filtered.map((t) => (
            <tr key={t.id}>
        
              <td>{t.title}</td>
              <td>{t.version}</td>

              <td>
                <span className={t.isActive ? "active" : "inactive"}>
                  {t.isActive ? "Active" : "Inactive"}
                </span>
              </td>

              <td>{new Date(t.createdAt).toLocaleDateString()}</td>

              {/* ==================== UPDATED ACTION BUTTONS ==================== */}
              <td>
                <div className="action-icons">
                  <button className="icon-btn" onClick={() => setViewItem(t)}>
                    <Eye size={16} />
                  </button>
                  <button className="icon-btn" onClick={() => handleEdit(t)}>
                    <Pencil size={16} />
                  </button>
                  <button className="icon-btn delete" onClick={() => handleDelete(t.id)}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ================= CREATE / EDIT MODAL ================= */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>

            <h2>{selected ? "Edit Terms" : "Create Terms"}</h2>

            <div className="form-group">
              <label>Title</label>
              <input
                value={form.title}
                onChange={(e) =>
                  setForm({ ...form, title: e.target.value })
                }
                placeholder="Enter title"
              />
            </div>

            <div className="form-group">
              <label>Version</label>
              <input
                value={form.version}
                onChange={(e) =>
                  setForm({ ...form, version: e.target.value })
                }
                placeholder="Enter version"
              />
            </div>

            <div className="form-group">
              <label>Content</label>
              <div className="ckeditor-wrapper">
                <CKEditor
                  editor={ClassicEditor as any}
                  config={editorConfig}
                  data={form.content}
                  onChange={(event, editor) => {
                    const data = editor.getData();
                    setForm({ ...form, content: data });
                  }}
                />
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-close" onClick={() => setShowModal(false)}>Close</button>
              <button className="btn-save" onClick={handleSave}>
                {selected ? "Update" : "Create"}
              </button>
            </div>

          </div>
        </div>
      )}


      {/* ================= VIEW MODAL ================= */}
      {viewItem && (
        <div className="modal-overlay" onClick={() => setViewItem(null)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>

            <h2>Terms Details</h2>

            <div className="detail-row">
              <span className="detail-label">Title:</span>
              <span className="detail-value">{viewItem.title}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Version:</span>
              <span className="detail-value">{viewItem.version}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Status:</span>
              <span className="detail-value">
                <span className={viewItem.isActive ? "active" : "inactive"}>
                  {viewItem.isActive ? "Active" : "Inactive"}
                </span>
              </span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Date:</span>
              <span className="detail-value">
                {new Date(viewItem.createdAt).toLocaleString()}
              </span>
            </div>

            {/* CONTENT SECTION */}
            <div className="form-group" style={{ marginTop: '20px' }}>
              <label className="content-label">CONTENT</label>
              <div
                className="terms-content-box"
                dangerouslySetInnerHTML={{ __html: viewItem.content }}
              />
            </div>

            <div className="modal-actions">
              <button className="btn-close" onClick={() => setViewItem(null)}>Close</button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default TermsPage;